import React, { useState, useEffect, useCallback } from 'react';
import './transactions.css';
import ModernHeader from '../../../../components/layout/Headers/ModernHeader';
import useNavigation from '../../../../shared/utils/useNavigation';
import { isUserLoggedIn } from '../../../../shared/utils/function';
import { clearNavigationState } from '../../../../shared/utils/navigationState';
import * as SwalHandled from '../../../../shared/utils/swalHandler';
import { getTransactions } from '../../../../shared/actions/transactionAction';
import TransactionItem from '../../../../features/cards/TransactionItem';
import { Pagination } from '../../../../shared/utils/paginationUtil';
import { CONFIG } from '../../../../shared/constants/common';
import { reportError } from '../../../../shared/sentry';

// ============================================================================
// CONSTANTS
// ============================================================================

const TRANSACTIONS_PER_PAGE = 5;

/**
 * Loading State Component
 */
const LoadingState = () => (
  <div className="modern-transactions-loading">
    <div className="modern-transactions-loading-spinner"></div>
    <p className="modern-transactions-loading-text">Loading transactions...</p>
  </div>
);

/**
 * Empty State Component
 */
const EmptyState = () => (
  <div className="modern-transactions-empty">
    <div className="modern-transactions-empty-icon">
      <i className="fa-solid fa-receipt"></i>
    </div>
    <h3 className="modern-transactions-empty-title">No Transactions Found</h3>
    <p className="modern-transactions-empty-description">
      You haven't made any transactions yet. Start exploring content to see your transaction history here.
    </p>
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ModernTransactions = () => {
  const navigate = useNavigation();
  const isLoggedIn = isUserLoggedIn();

  // State
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    if (!isLoggedIn) {
      clearNavigationState();
      navigate.toLogin();
    }
  }, [isLoggedIn, navigate]);

  const loadTransactions = useCallback(
    async (page = 1) => {
      if (!isLoggedIn) return;

      setIsLoading(true);

      try {
        const skip = page - 1;

        const response = await getTransactions({
          limit: TRANSACTIONS_PER_PAGE,
          skip: skip,
        });

        if (response.success && response.data) {
          setTransactions(response.data.data || []);
          setTotalPages(response.data.totalPages || 1);
          setCurrentPage(page);
        } else {
          throw new Error(response.error || 'Failed to fetch transactions');
        }
      } catch (error) {
        reportError(error, 'transactions load');
        setTransactions([]);
        setTotalPages(1);

        await SwalHandled.showWarning(
          'Unable to Load Transactions',
          "We couldn't load your transaction history. Please try again later."
        );
      } finally {
        setIsLoading(false);
      }
    },
    [isLoggedIn]
  );

  useEffect(() => {
    if (isLoggedIn) {
      loadTransactions(1);
    }
  }, [isLoggedIn, loadTransactions]);

  const handlePageChange = useCallback(
    (page) => {
      if (page >= 1 && page <= totalPages && page !== currentPage) {
        loadTransactions(page);
      }
    },
    [currentPage, totalPages, loadTransactions]
  );

  const handlePreviousPage = useCallback(() => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  }, [currentPage, handlePageChange]);

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  }, [currentPage, totalPages, handlePageChange]);

  const handleViewSubscriptions = useCallback(() => {
    clearNavigationState();
    navigate.toSubscriptions();
  }, [navigate]);

  if (!isLoggedIn) {
    return null;
  }

  return (
    <div className="modern-transactions-page">
      <ModernHeader
        customTitle="Transactions"
        showButtonBorder={false}
        hideSearchIcon
        showAvatar={false}
        showBackButton
      />

      <main className="modern-transactions-main">
        <div className="modern-transactions-description">
          <p className="modern-transactions-description-text">
            View your purchase history, payment status, and transaction details.
          </p>
        </div>

        <div className="modern-transactions-payment-notice">
          <p className="modern-transactions-notice-text">
            The charges on your credit card statement will appear as{' '}
            <span className="modern-transactions-notice-highlight">*CCBill.com Prestige Int*</span>
          </p>
        </div>

        <div className="modern-transactions-subscription-button">
          <button className="modern-transactions-view-subscriptions-btn" onClick={handleViewSubscriptions}>
            <i className="fa-solid fa-credit-card modern-transactions-btn-icon"></i>
            View Subscriptions
          </button>
        </div>

        {isLoading ? (
          <LoadingState />
        ) : (
          <>
            <div className="modern-transactions-list">
              {transactions.length > 0 ? (
                transactions.map((transaction, index) => (
                  <TransactionItem
                    key={transaction._id || index}
                    transaction={transaction}
                  />
                ))
              ) : (
                <EmptyState />
              )}
            </div>

            {transactions.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                onPreviousPage={handlePreviousPage}
                onNextPage={handleNextPage}
              />
            )}

            <div className="modern-transactions-footer">
              <div className="modern-transactions-footer-text">
                <small>
                  For billing inquiries please visit{' '}
                  <a href={CONFIG.CCBILL_URL} target="_blank" rel="noopener noreferrer">
                    ccbill.com
                  </a>
                </small>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default ModernTransactions;
